import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  InputNumber,
  Typography,
  Row,
  Col,
  Empty,
  Popconfirm,
  message,
  Badge,
  Space,
  Divider,
  Statistic,
  Modal,
  Spin
} from 'antd';
import {
  DeleteOutlined,
  ShoppingCartOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { cartService } from '../../services/cartService';
import { Cart, CartItem, Order } from '../../types/cart.types';
import { useHistory } from 'react-router-dom';
import StripeProvider from '../../components/payments/StripeProvider';
import PaymentMethodSelection from '../../components/payments/PaymentMethodSelection';
import { useTranslation } from 'react-i18next';
import {FEATURE_STRIPE} from '../../appConfig';
const { Title, Text } = Typography;

const AgencyCart: React.FC = () => {
  const { t } = useTranslation();
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkoutModalVisible, setCheckoutModalVisible] = useState(false);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [completedOrder, setCompletedOrder] = useState<Order | null>(null);
  const [stripeModalVisible, setStripeModalVisible] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [balanceInfo, setBalanceInfo] = useState<{
    sufficient: boolean;
    currentBalance: number;
    requiredAmount: number;
  } | null>(null);
  const [checkingBalance, setCheckingBalance] = useState(false);

  const history = useHistory();

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = () => {
    try {
      setLoading(true);
      const currentCart = cartService.getCart();
      setCart(currentCart);
    } catch (error) {
      message.error(t('agency.cart.loadError'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateQuantity = (itemId: string, quantity: number) => {
    try {
      const updatedCart = cartService.updateItemQuantity(itemId, quantity);
      setCart(updatedCart);
    } catch (error) {
      message.error(t('agency.cart.updateQuantityError'));
      console.error(error);
    }
  };

  const handleRemoveItem = (itemId: string) => {
    try {
      const updatedCart = cartService.removeItem(itemId);
      setCart(updatedCart);
      message.success(t('agency.cart.removeSuccess'));
    } catch (error) {
      message.error(t('agency.cart.removeError'));
      console.error(error);
    }
  };

  const handleClearCart = () => {
    try {
      const emptyCart = cartService.clearCart();
      setCart(emptyCart);
      message.success(t('agency.cart.clearSuccess'));
    } catch (error) {
      message.error(t('agency.cart.clearError'));
      console.error(error);
    }  };
  
  const handleStripePaymentSuccess = async (paymentIntentId: string) => {
    if (!cart) return;

    setProcessingPayment(true);
    try {
      const order = await cartService.processCardPayment(paymentIntentId);

      // Créer les vouchers pour chaque pack du panier

        const res = await cartService.createVoucherByCard(
          paymentIntentId,
          `Voucher pour ${cart.items.map(item => item.packName).join(', ')} - Commande #${order.id}`
        ,
          cart.items.map(item => ({
            packId: item.packId,
            quantity: item.quantity
          }))
          
        );

        if (!res.success) {
          message.warning(`Problème avec la commande ${order.id} : ${res.message}`);
        }
      

      setCompletedOrder(order);
      setStripeModalVisible(false);
      setSuccessModalVisible(true);
      loadCart(); // recharger panier vide
      message.success(t('agency.cart.orderValidated', { count: order.voucherIds?.length || 0 }));
    } catch (error) {
      message.error(t('agency.cart.paymentFinalizeError'));
      console.error(error);
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleStripePaymentError = (error: any) => {
    message.error(t('agency.cart.paymentErrorMessage', { message: error.message || '' }));
    console.error('Stripe payment error:', error);
  };
  // Nouvelles fonctions pour le paiement par solde
  const checkBalanceForCart = useCallback(async () => {
    if (!cart) return;
    
    setCheckingBalance(true);
    try {
      const balanceCheck = await cartService.checkBalance();
      setBalanceInfo(balanceCheck);
    } catch (error) {
      console.error('Erreur lors de la vérification du solde:', error);
      setBalanceInfo({
        sufficient: false,
        currentBalance: 0,
        requiredAmount: cart.totalAmount
      });
    } finally {
      setCheckingBalance(false);
    }
  }, [cart]);

  const processBalancePayment = async () => {
    if (!cart) return;

    setProcessingPayment(true);
    try {
      const order = await cartService.checkout('BALANCE');
      setCompletedOrder(order);
      setCheckoutModalVisible(false);
      setSuccessModalVisible(true);
      loadCart(); // Recharger le panier vide
      message.success(t('agency.cart.orderValidated', { count: order.voucherIds?.length || 0 }));
    } catch (error) {
      message.error(error instanceof Error ? error.message : t('agency.cart.balancePaymentError'));
      console.error('Balance payment error:', error);
    } finally {
      setProcessingPayment(false);
    }
  };
  // Vérifier le solde quand le modal s'ouvre
  useEffect(() => {
    if (checkoutModalVisible && cart && !balanceInfo) {
      checkBalanceForCart();
    }
  }, [checkoutModalVisible, cart, checkBalanceForCart,balanceInfo]);

  const columns = [
    {
      title: t('agency.cart.packColumn'),
      dataIndex: 'packName',
      key: 'packName',
      width: '25%',
    },
    {
      title: t('agency.cart.unitPriceColumn'),
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: '15%',
      render: (price: number) => `${price?.toFixed(2)} €`,
    },
    {
      title: t('agency.cart.quantityColumn'),
      key: 'quantity',
      width: '20%',
      render: (_, record: CartItem) => (
        <InputNumber
          min={1}
          max={100}
          value={record.quantity}
          onChange={(value) => handleUpdateQuantity(record.id, value as number)}
          style={{ width: 80 }}
        />
      ),
    },
    {
      title: t('common.total'),
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      width: '15%',
      render: (price: number) => `${price?.toFixed(2)} €`,
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: '15%',
      render: (_, record: CartItem) => (
        <Popconfirm
          title={t('agency.cart.removeConfirm')}
          onConfirm={() => handleRemoveItem(record.id)}
          okText={t('common.yes')}
          cancelText={t('common.no')}
        >
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
          >
            {t('agency.cart.remove')}
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const renderEmptyCart = () => (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={t('agency.cart.emptyCart')}
    >
      <Button
        type="primary"
        onClick={() => history.push('/agency/packs')}
      >
        {t('agency.cart.browsePacks')}
      </Button>
    </Empty>
  );
  const renderCheckoutModal = () => (
    <Modal
      title={t('agency.cart.checkoutTitle')}
      visible={checkoutModalVisible}
      onCancel={() => setCheckoutModalVisible(false)}
      footer={null}
      width={600}
    >      <div style={{ marginBottom: 20 }}>
        <PaymentMethodSelection
          totalAmount={cart?.totalAmount || 0}          
          onSelectCardPayment={() => {
            setCheckoutModalVisible(false);
            setStripeModalVisible(true);
          }}
          onSelectBankTransfer={() => {
            setCheckoutModalVisible(false);
            history.push('/agency/manual-payment');
          }}
          onSelectBalancePayment={processBalancePayment}
          balanceInfo={balanceInfo}
          checkingBalance={checkingBalance}
        />
      </div>
      
      <Divider />
      
      <div>
        <Title level={5}>{t('agency.cart.orderSummary')}</Title>
        <Row justify="space-between">
          <Col>{t('agency.cart.itemCount')}</Col>
          <Col>{cart?.items.length || 0}</Col>
        </Row>
        <Row justify="space-between">
          <Col>{t('agency.cart.totalQuantity')}</Col>
          <Col>{cart?.items.reduce((sum, item) => sum + item.quantity, 0) || 0}</Col>
        </Row>
        <Divider />
        <Row justify="space-between">
          <Col><Text strong>{t('agency.cart.totalAmount')}</Text></Col>
          <Col><Text strong>{cart?.totalAmount?.toFixed(2)} €</Text></Col>
        </Row>
      </div>
    </Modal>
  );

  const renderStripeModal = () => (
    <Modal
      title={t('agency.cart.payByCardTitle')}
      visible={stripeModalVisible}
      onCancel={() => setStripeModalVisible(false)}
      footer={null}
      destroyOnClose
    >
      <Spin spinning={processingPayment} tip={t('agency.cart.paymentProcessing')}>
        <div>
          <p>{t('agency.cart.cardPaymentInstructions')}</p>
            {cart && (
            <StripeProvider
              publicKey={process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_live_51RALVIJdaH5TMNaHtPCbNAya0IPskKnyPx9D7e8ZUmgBJQG6ZXBGcDp1ifgmPVG9iI0ikRUmQZWXm4MxcfE3OoB600txHNevf4'}
              amount={cart.totalAmount}
              onSuccess={handleStripePaymentSuccess}
              onError={handleStripePaymentError}
            />
          )}
          
          <div style={{ marginTop: 16 }}>
            <Button onClick={() => setStripeModalVisible(false)}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      </Spin>
    </Modal>
  );

  const renderSuccessModal = () => (
    <Modal
      title={<div style={{ textAlign: 'center' }}><CheckCircleOutlined style={{ color: '#52c41a', fontSize: 32 }} /></div>}
      visible={successModalVisible}
      centered
      onCancel={() => setSuccessModalVisible(false)}
      footer={[
        <Button key="orders" onClick={() => history.push('/agency/orders')}>
          {t('agency.cart.viewOrders')}
        </Button>,
        <Button key="continue" type="primary" onClick={() => {
          setSuccessModalVisible(false);
          history.push('/agency/packs');
        }}>
          {t('agency.cart.continueShopping')}
        </Button>,
      ]}
    >
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <Title level={4} style={{ color: '#52c41a' }}>{t('agency.cart.orderSuccessTitle')}</Title>
        <p>{t('agency.cart.orderConfirmed', { id: completedOrder?.id })}</p>
        <p>{t('agency.cart.paymentMethodLabel')} {
          {
            'CASH': t('agency.cart.payMethodCash'),
            'CARD': t('agency.cart.payMethodCard'),
            'BANK_TRANSFER': t('agency.cart.payMethodBankTransfer')
          }[completedOrder?.paymentMethod || 'CASH']
        }</p>
        <p>{t('agency.cart.paymentStatusLabel')} {
          {
            'PENDING': t('agency.cart.payStatusPending'),
            'COMPLETED': t('agency.cart.payStatusCompleted'),
            'FAILED': t('agency.cart.payStatusFailed')
          }[completedOrder?.paymentStatus || 'PENDING']
        }</p>
        <p>{t('agency.cart.totalAmount')} {completedOrder?.totalAmount?.toFixed(2)} €</p>
      </div>
    </Modal>
  );

  return (
    <>
      <Card
        title={(
          <Space>
            <ShoppingCartOutlined />
            <span>{t('agency.cart.myCart')}</span>
            {cart && cart.items.length > 0 && (
              <Badge count={cart.items.length} style={{ backgroundColor: '#1890ff' }} />
            )}
          </Space>
        )}
        extra={
          cart && cart.items.length > 0 && (
            <Popconfirm
              title={t('agency.cart.clearCartConfirm')}
              onConfirm={handleClearCart}
              okText={t('common.yes')}
              cancelText={t('common.no')}
            >
              <Button danger>{t('agency.cart.clearCart')}</Button>
            </Popconfirm>
          )
        }
      >
        {cart && cart.items.length > 0 ? (
          <>
            <Table
              columns={columns}
              dataSource={cart.items}
              loading={loading}
              pagination={false}
              rowKey="id"
            />

            <Row justify="end" style={{ marginTop: 24 }}>
              <Col span={8}>
                <Card>
                  <Statistic
                    title={t('agency.cart.totalAmount')}
                    value={cart.totalAmount}
                    precision={2}
                    suffix="€"
                  />
                  <Button
                    type="primary"
                    size="large"
                    block
                    style={{ marginTop: 16 }}
                    onClick={() => setCheckoutModalVisible(true)}
                  >
                    {t('agency.cart.placeOrder')}
                  </Button>
                </Card>
              </Col>
            </Row>
          </>
        ) : (
          renderEmptyCart()
        )}
      </Card>

      {renderCheckoutModal()}
      {FEATURE_STRIPE && renderStripeModal()}
      {renderSuccessModal()}
    </>
  );
};

export default AgencyCart;
